import { type MouseEvent, useEffect, useState, useRef } from "react";
import Head from "next/head";
import { useForm, useWatch } from "react-hook-form";
import type * as z from "zod";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";

import {
  Heading,
  Button,
  Box,
  Collapse,
  Step,
  Select,
  TextField,
  Link,
  Text,
  TextAreaField,
  Loader,
  IconFa,
  ErrorText,
  Totaliser,
} from "@cruk/cruk-react-components";
import { useRouter } from "next/compat/router";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { fetcher } from "@fwa/src/services/apiClient";
import {
  urlNewDonor,
  fwsUrlNewDonation,
} from "@fwa/src/services/donationService";
import { fwsUrlPage } from "@fwa/src/services/pageService";
import { queryAsString } from "@fwa/src/utils/urlUtils";
import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";
import { sendGTMTrackingEvent } from "@fwa/src/services/gtm";
import {
  regexEmail,
  regexFornameExclusions,
  regexSurnameExclusions,
  sanitiseIfUkPostCode,
  sanitiseNames,
  splitMobileAndLandlineNumbers,
} from "@fwa/src/utils/formUtils";
import useIsTabActive from "@fwa/src/hooks/useIsTabActive";
import { useOptimizelyContext } from "@fwa/src/contexts/OptimizelyContext";
import { getBrandForPage } from "@fwa/src/utils/pageUtils";
import { donateValidation } from "@fwa/src/validation/donatation";

import { ErrorPageWrapper } from "@fwa/src/components/ErrorPageWrapper";
import { AddressFields } from "@fwa/src/components/AddressFields";
import { SuppressionFields } from "@fwa/src/components/SuppressionFields";
import { DonatePageGiftAid } from "@fwa/src/components/DonatePageGiftAid";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/DonatePage/styles.module.css";

import {
  type DonorType,
  type AllDonatablePageType,
  type BrandNameType,
  type DonationNewType,
} from "@fwa/src/types";

export type DonateValues = z.infer<typeof donateValidation>;
export type DonateValuesKeysType = keyof DonateValues;
type TitlesType = z.infer<typeof donateValidation>["title"];
const defaultTitles: TitlesType[] = ["Mr", "Mrs", "Miss", "Ms", "Mx", "Other"];

export const DonatePage = () => {
  const { trackEventOptimizely, optimizelyContext } = useOptimizelyContext();
  const { optimizelyClient } = optimizelyContext;
  const { trackError, trackEventGtm } = useTracking();
  const [fundraiserState] = useFundraiserContext();
  const isTabVisible = useIsTabActive();
  const { fundraiser } = fundraiserState;
  const maxCharacters = 150;
  const mountCount = useRef(1);

  const router = useRouter();
  const { slug } = router?.query || {};
  const slugString = queryAsString(slug);
  const url = slugString.length
    ? `${fwsUrlPage({ pageId: slugString })}`
    : null;
  const { data: page, error } = useSwrGet<AllDonatablePageType>(url);
  const themeName = page
    ? (page?.theme as BrandNameType) || getBrandForPage(page)
    : null;
  const [brandName, setBrandName] = useBrandContext();
  const isSU2CBrand = brandName === "su2c";
  const showTotaliser = page?.target
    ? page?.target > page?.donationsTotalAmount
    : false;

  const titleFromFundraiser = !fundraiser?.title
    ? ""
    : defaultTitles.includes(fundraiser.title as TitlesType)
      ? (fundraiser.title as TitlesType)
      : "Other";
  const titleOtherFromFundraiser = !fundraiser?.title
    ? undefined
    : defaultTitles.includes(fundraiser.title as TitlesType)
      ? undefined
      : fundraiser.title;

  const initialValues: DonateValues = {
    amount: "",
    title: titleFromFundraiser,
    titleOther: titleOtherFromFundraiser,
    email: fundraiser?.emailAddress || "",
    forename: fundraiser?.forename || "",
    surname: fundraiser?.surname || "",
    address: {
      country: "GBR",
      line1: fundraiser?.address?.line1 || "",
      line2: fundraiser?.address?.line2 || "",
      line3: fundraiser?.address?.line3 || "",
      city: fundraiser?.address?.city || "",
      postalCode: fundraiser?.address?.postalCode || "",
      validated: fundraiser?.address?.validated || false,
    },
    phoneNumber: fundraiser?.telephoneNumberMobile || "",
    donationMessage: "",
    donationName:
      page?.owner?.displayName ||
      `${fundraiser?.forename || ""} ${fundraiser?.surname || ""}`,
    personalGiftAid: null,
    noBenefitReceived: null,
    toBeGiftAided: false,
    suppressions: {
      email: "PUEM",
      phone: "LIPH",
      post: "LIPO",
      text: "PUSM",
    },
  };

  const [submitionErrorMessage, setSubmitionErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [hasBeenHidden, setHasBeenHidden] = useState(false);
  const [showRefreshButton, setShowRefreshButton] = useState(false);

  const formProps = useForm<DonateValues>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialValues,
    criteriaMode: "firstError",
    shouldFocusError: true,
    resolver: standardSchemaResolver(donateValidation),
    resetOptions: {
      keepDirtyValues: true, // user-interacted input will be retained
      keepErrors: false, // input errors will be retained with value update
    },
  });

  const {
    register,
    setValue,
    formState: { errors, touchedFields },
    control,
    setError,
    reset,
    trigger,
    getValues,
    setFocus,
  } = formProps;

  const watchAmount = useWatch({ control, name: "amount" });
  const watchTitle = useWatch({ control, name: "title" });
  const watchForename = useWatch({ control, name: "forename" });
  const watchSurname = useWatch({ control, name: "surname" });
  const watchMessage = useWatch({ control, name: "donationMessage" });
  const watchPersonalGiftAid = useWatch({ control, name: "personalGiftAid" });
  const watchNoBenefitReceived = useWatch({
    control,
    name: "noBenefitReceived",
  });
  const watchSuppressions = useWatch({ control, name: "suppressions" });
  const phoneNumberRequired =
    ["LIPH"].includes(watchSuppressions.phone) ||
    ["PYSM"].includes(watchSuppressions.text);

  function handleSubmitClick() {
    if (!phoneNumberRequired) {
      setValue("phoneNumber", "");
    }
    trigger(undefined, { shouldFocus: true }) // triggers validation on all fields
      .then((valid) => {
        if (valid) {
          submit();
          return undefined;
        } else {
          console.log({ errors });
        }
        return undefined;
      })
      .catch((err: Error) => {
        trackError(err, {
          component: "DonatePage",
          function: "handleFormSubmitClick",
        });
        setSubmitionErrorMessage("unable to submit");
        setSubmitting(false);
      });
  }

  function handleFWSDonorSubmitError(errorWithMessage: { message: string }) {
    // first set the field level errors if we can identify them from the FWS error message
    if (
      errorWithMessage.message.includes(
        "This value is not a valid email address",
        0,
      )
    ) {
      setError("email", { message: regexEmail.message });
      setSubmitting(false);
    }
    if (
      errorWithMessage.message.includes(
        "Please only enter a formal name here",
        0,
      )
    ) {
      setError("forename", { message: regexFornameExclusions.message });
      setError("surname", { message: regexSurnameExclusions.message });
      setSubmitting(false);
    }
    if (
      errorWithMessage.message.includes(
        "Each of your address lines must be different",
        0,
      )
    ) {
      setError("address.line2", {
        message:
          "Address line 1, Address line 2, Address line 3 should not match",
      });
      setSubmitting(false);
    } else {
      setSubmitionErrorMessage("unable to create donor");
      setSubmitting(false);
    }

    // if we have any errors, set focus to the first field with an error
    const errorsvalues = Object.values(errors);
    if (errorsvalues.length) {
      setSubmitting(false);
      setFocus(Object.keys(errors)[0] as DonateValuesKeysType);
    }
  }

  async function submit() {
    setSubmitionErrorMessage("");
    setSubmitting(true);
    const values = getValues();

    // if user has not opted in to phone or text suppressions,
    // then we have no legal basis to collect their phone number
    const phoneNumber =
      phoneNumberRequired && values.phoneNumber
        ? values.phoneNumber
        : undefined;
    const { telephoneNumberMobile, telephoneNumberLandline } =
      splitMobileAndLandlineNumbers(phoneNumber);

    // Using donor values create new donor
    const newDonor: Partial<DonorType> = {
      forename: sanitiseNames(values.forename),
      surname: sanitiseNames(values.surname),
      title: values.title === "Other" ? values.titleOther : values.title,
      address: {
        ...values.address,
        country: values.address.country || "GBR",
        postalCode: sanitiseIfUkPostCode(values.address.postalCode),
      },
      emailAddress: values.email,
      telephoneNumberMobile,
      telephoneNumberLandline,
      suppressions: Object.values(values.suppressions).filter(
        (value: string) => value.length,
      ),
    };

    // create donor
    const donor: DonorType | void = await fetcher<DonorType>(urlNewDonor, {
      method: "POST",
      body: JSON.stringify(newDonor),
    }).catch((err) => {
      // in case we get errors from FWS, lets help the
      trackError(err as Error, {
        component: "DonatePageForm",
        function: "createDonor",
      });
      const errorWithMessage = err as { message: string };
      handleFWSDonorSubmitError(errorWithMessage);
    });

    if (!donor) {
      setSubmitionErrorMessage("unable to create donor");
      setSubmitting(false);
      return undefined;
    }

    // Using donation values create new donation
    const newDonation: DonationNewType = {
      amount: parseFloat(values.amount),
      donationMessage: values.donationMessage || "",
      donationName: values.donationName || "",
      personalGiftAid: values.personalGiftAid === "true",
      result: 1,
      toBeGiftAided: values.toBeGiftAided,
    };

    // create donation
    const donation: {
      dotsUrl: string;
    } | void = await fetcher<{
      dotsUrl: string;
    }>(
      fwsUrlNewDonation({
        pageId: page?.uniqueId || "",
        donorId: donor.uniqueId,
      }),
      {
        method: "POST",
        body: JSON.stringify(newDonation),
      },
    ).catch((err) => {
      trackError(err as Error, {
        component: "DonatePage",
        function: "createDonation",
      });
    });

    if (!donation?.dotsUrl) {
      setSubmitionErrorMessage("unable to create donation");
      setSubmitting(false);
      return undefined;
    }

    try {
      trackEventOptimizely({
        eventKey: "of_donate_submit",
        eventTags: {
          value: parseFloat(values.amount || "0"),
        },
      });
      // track suppressions field options
      if (values.suppressions.email === "PYEM") {
        trackEventOptimizely({
          eventKey: "of_opt_in_email",
        });
      }
      if (values.suppressions.phone === "LIPH") {
        trackEventOptimizely({
          eventKey: "of_opt_in_phone",
        });
      }
      if (values.suppressions.text === "PYSM") {
        trackEventOptimizely({
          eventKey: "of_opt_in_sms",
        });
      }
      if (values.suppressions.post === "LIPO") {
        trackEventOptimizely({
          eventKey: "of_opt_in_post",
        });
      }
      await optimizelyClient?.close().catch((err) => {
        console.log(err as Error, {
          component: "DonatePage",
          function: "optimizelyClientClose",
        });
      });
    } catch (err) {
      trackError(err as Error, {
        component: "DonatePage",
        function: "sendOptimizelyTracking",
      });
    }

    if (donation.dotsUrl) {
      setRedirecting(true);
      return router?.push(donation.dotsUrl);
    }
    return undefined;
  }

  useEffect(() => {
    if (!!themeName && themeName !== brandName) {
      setBrandName(themeName);
    }
  }, [themeName, brandName, setBrandName]);

  useEffect(() => {
    if (!trackEventGtm) return;
    if (mountCount.current === 1) {
      sendGTMTrackingEvent({
        event: "pageView",
        pagePath: "/donate",
        pageTitle: "donate",
      });

      // GTM event to show first bit of form has been loaded
      trackEventGtm({
        event: "eec.checkout",
        ecommerce: {
          checkout: {
            actionField: {
              step: 1,
            },
            products: [
              {
                id: "fundraising donation",
                name: "fundraising donation",
                category: "donation",
                variant: "",
                brand: brandName,
                quantity: 1,
                price: 0,
              },
            ],
          },
        },
      });
    }
    mountCount.current += 1;
  }, [trackEventGtm, brandName]);

  useEffect(() => {
    if (!trackEventGtm) return;
    if (touchedFields.amount) {
      const amount: number = parseInt(watchAmount, 10) || 0;
      trackEventGtm({
        event: "eec.checkout",
        ecommerce: {
          checkout: {
            actionField: {
              step: 2,
            },
            products: [
              {
                id: "fundraising donation",
                name: "fundraising donation",
                category: "donation",
                variant: "",
                brand: brandName,
                quantity: 1,
                price: amount,
              },
            ],
          },
        },
      });
    }
  }, [watchAmount]);

  // we might get fundraiser details from fundraising context late
  // in which case reset form with new fundraiser details to correctly prefill fields
  useEffect(() => {
    reset(initialValues);
  }, [fundraiser, page]);

  // useEffect to detect when tab is visible and clean up state from previous navigation
  useEffect(() => {
    if (!isTabVisible) {
      setHasBeenHidden(true);
    }
    if (isTabVisible && hasBeenHidden && redirecting) {
      setShowRefreshButton(true);
    }
  }, [isTabVisible]);

  return (
    <ErrorPageWrapper error={error!}>
      <Head>
        <title>{`Donate to ${page?.title || ""}`}</title>
      </Head>
      <div className={sharedStyles["content-max-width"]}>
        <div
          className={[
            styles["page-wrapper-responsive"],
            "spacing-props",
            "color-props",
          ].join(" ")}
          data-bg-color="background-light"
          data-padding-top="s"
          data-padding-horizontal="none"
          data-padding-bottom="xxl"
          data-margin-bottom="m"
        >
          <div
            className={[
              "spacing-props",
              sharedStyles["content-wrapper-responsive"],
            ].join(" ")}
            data-padding-vertical="m"
          >
            {!!page && (
              <div className={sharedStyles["form-wrapper"]}>
                <Heading textAlign="center" h1>
                  {`Donate to ${page.title}`}
                </Heading>
                <Box marginBottom="xl">
                  <Step current={1} steps={["Details", "Payment"]} />
                </Box>
                {fundraiserState?.loggedInStatus === "unknown" ? (
                  <Loader />
                ) : (
                  <>
                    <form
                      noValidate
                      data-component="donate-form"
                      method="dialog"
                    >
                      <fieldset
                        className={sharedStyles["fieldset"]}
                        data-component="donate-amount-section"
                      >
                        <Box paddingBottom="l" margin="none">
                          <Heading h2 as="legend" paddingLeft="none">
                            Donation amount
                          </Heading>
                          <div
                            className={[
                              styles["donation-amount-row"],
                              "spacing-props",
                            ].join(" ")}
                            data-margin-bottom="xs"
                          >
                            {[100, 50, 20].map((amount) => (
                              <button
                                className={styles["donate-amount-button"]}
                                key={amount}
                                type="button"
                                data-is-active={
                                  parseInt(watchAmount, 10) === amount
                                }
                                data-is-su2c-brand={isSU2CBrand}
                                onClick={() => {
                                  setValue("amount", amount.toString(), {
                                    shouldTouch: true,
                                    shouldValidate: true,
                                  });
                                  trackEventGtm({
                                    event: "nudge_select",
                                    amount,
                                  });
                                }}
                                data-cta-type="donate-amount-toggle"
                              >
                                <span>£{amount}</span>
                              </button>
                            ))}
                          </div>
                          <Text textWeight="labels" marginTop="none">
                            Or enter another amount
                          </Text>
                          <TextField
                            {...register("amount")}
                            label=""
                            extraLeft="£"
                            required
                            type="text" // we don't want number type because of its up/down arrows
                            inputMode="decimal"
                            pattern="[0-9.]*"
                            aria-label="Amount"
                            hasError={!!errors.amount?.message?.length}
                            onFocus={() => {
                              trackEventOptimizely({
                                eventKey: "enter_custom_donation_amount",
                              });
                            }}
                          />
                          <ErrorText>{errors.amount?.message}</ErrorText>

                          {showTotaliser ? (
                            <Box data-component="donate-form-totaliser">
                              <Totaliser
                                total={page.donationsTotalAmount || 0}
                                target={page?.target}
                                additionalAmount={parseFloat(watchAmount) || 0}
                              />
                            </Box>
                          ) : null}

                          <Box marginTop="xxs">
                            <Collapse
                              headerTitleText="How much of your donation comes to Cancer Research UK?"
                              id="donation-to-cruk"
                            >
                              <Text>
                                The only fee deducted from your donation is the
                                payment processing fee. This is the fee we’re
                                charged by your credit or debit card provider,
                                or by PayPal UK when you donate. The fee varies
                                depending on how you choose to pay. &nbsp;
                              </Text>
                              <Text>
                                <Link
                                  href="https://www.cancerresearchuk.org/get-involved/do-your-own-fundraising/practical-fundraising-help/set-up-a-cancer-research-uk-giving-page/fees-and-charges"
                                  data-cta-type="link-mail-to-fees-and-charges"
                                >
                                  Fees and charges for Cancer Research UK Giving
                                  Pages.
                                </Link>
                              </Text>
                            </Collapse>
                          </Box>
                        </Box>
                      </fieldset>

                      <fieldset
                        className={sharedStyles["fieldset"]}
                        data-component="details-section"
                      >
                        <Heading h2 as="legend">
                          Your Details
                        </Heading>
                        <div
                          className={sharedStyles["field-wrapper"]}
                          data-input-width="medium"
                        >
                          <Select
                            {...register("title")}
                            label="Title"
                            onChange={(e) => {
                              if (e.target.value !== "Other") {
                                setValue("titleOther", undefined);
                              }
                              // run default onChange for field
                              register("title")
                                .onChange(e)
                                .catch(() => {
                                  // don't let browser element onChange error take down whole form
                                });
                            }}
                          >
                            <option value="">Please Select</option>
                            {defaultTitles.map((title) => {
                              if (typeof title !== "string") return undefined;
                              return (
                                <option key={`title-${title}`} value={title}>
                                  {title}
                                </option>
                              );
                            })}
                          </Select>
                        </div>
                        {watchTitle === "Other" ? (
                          <div
                            className={sharedStyles["field-wrapper"]}
                            data-input-width="medium"
                          >
                            <TextField
                              {...register("titleOther")}
                              label="Please tell us your title"
                              required
                              errorMessage={errors.titleOther?.message}
                            />
                          </div>
                        ) : null}

                        <div
                          className={sharedStyles["field-wrapper"]}
                          data-input-width="medium"
                        >
                          <TextField
                            {...register("forename")}
                            label="First name"
                            required
                            onBlur={(e) => {
                              // prefill the donor name in the donation with forename
                              setValue(
                                "donationName",
                                `${e.target.value} ${watchSurname}`,
                                { shouldTouch: true, shouldValidate: true },
                              );
                              // run default onBlur for field
                              register("forename")
                                .onBlur(e)
                                .catch(() => {
                                  // don't let browser element blur error break entire form
                                });
                            }}
                            errorMessage={errors.forename?.message}
                            hintText={
                              "Please use your legal first and last names."
                            }
                          />
                        </div>
                        <div
                          className={sharedStyles["field-wrapper"]}
                          data-input-width="medium"
                        >
                          <TextField
                            {...register("surname")}
                            label="Last name"
                            required
                            onBlur={(e) => {
                              // prefill the donor name in the donation with surname
                              setValue(
                                "donationName",
                                `${watchForename} ${e.target.value}`,
                                { shouldTouch: true, shouldValidate: true },
                              );
                              // run default onBlur for field
                              register("surname")
                                .onBlur(e)
                                .catch(() => {
                                  // don't let browser element blur error take down whole form
                                });
                            }}
                            errorMessage={errors.surname?.message}
                          />
                        </div>
                        <div
                          className={sharedStyles["field-wrapper"]}
                          data-input-width="medium"
                        >
                          <TextField
                            {...register("email")}
                            name="email"
                            autoComplete="email"
                            label="Email address"
                            required
                            errorMessage={errors.email?.message}
                          />
                        </div>

                        <Box paddingBottom="xxl">
                          <AddressFields<DonateValues> {...formProps} />
                        </Box>
                      </fieldset>

                      <Box paddingBottom="xxl" paddingTop="s" margin="none">
                        <fieldset
                          className={sharedStyles["fieldset"]}
                          data-component="message-section"
                        >
                          <Heading h2 as="legend">
                            Leave a message with your donation
                          </Heading>
                          <div className={sharedStyles["field-wrapper"]}>
                            <TextField
                              {...register("donationName")}
                              label="Name you would like to appear on the Giving Page"
                              hintText="If you leave this field blank, only the person you are donating to will see your name"
                              errorMessage={errors.donationName?.message}
                              maxLength={101}
                            />
                          </div>
                          <div className={sharedStyles["field-wrapper"]}>
                            <TextAreaField
                              {...register("donationMessage")}
                              label="Your message"
                              lineCount={4}
                              maxLength={maxCharacters}
                              errorMessage={errors.donationMessage?.message}
                            />
                            <span style={{ float: "right" }}>
                              {maxCharacters - (watchMessage?.length || 0)}{" "}
                              characters remaining
                            </span>
                          </div>
                        </fieldset>
                      </Box>

                      <Box
                        paddingBottom="l"
                        margin="none"
                        data-component="suppressions-section"
                      >
                        <SuppressionFields
                          {...formProps}
                          suppressions={watchSuppressions}
                          child={
                            phoneNumberRequired ? (
                              <div
                                className={[
                                  sharedStyles["field-wrapper"],
                                  "spacing-props",
                                ].join(" ")}
                                data-margin-bottom="s"
                              >
                                <TextField
                                  {...register("phoneNumber")}
                                  label="Phone number"
                                  type="tel"
                                  required
                                  errorMessage={errors.phoneNumber?.message}
                                />
                                <Box marginTop="xs">
                                  <Collapse
                                    headerTitleText="Why are we asking for this?"
                                    id="tel-gdpr-explanation"
                                  >
                                    <Text marginBottom="s">
                                      We may use this information to identify if
                                      you’ve supported Cancer Research UK
                                      before. This will help us to ensure that
                                      our records are up to date so that you can
                                      receive appropriate communications from
                                      us.
                                    </Text>
                                  </Collapse>
                                </Box>
                              </div>
                            ) : null
                          }
                        />
                      </Box>

                      <fieldset
                        className={sharedStyles["fieldset"]}
                        data-component="gift-aid-section"
                      >
                        <Heading marginBottom="xxs" h2 as="legend">
                          Gift Aid
                        </Heading>
                        <DonatePageGiftAid<DonateValues>
                          {...formProps}
                          amount={watchAmount}
                          personalGiftAid={watchPersonalGiftAid}
                          noBenefitReceived={watchNoBenefitReceived}
                        />
                      </fieldset>
                      <Box marginTop="xl">
                        <div className={styles["submit-wrapper"]}>
                          <Button
                            type="button"
                            appearance="tertiary"
                            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                              e.preventDefault();
                              setValue("toBeGiftAided", false);
                              handleSubmitClick();
                            }}
                            disabled={submitting || redirecting}
                            data-cta-type="submit-without-gift-aid"
                          >
                            <IconFa faIcon={faChevronRight} />
                            Continue without Gift Aid
                          </Button>
                          <Button
                            type="button"
                            appearance="primary"
                            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                              e.preventDefault();
                              setValue("toBeGiftAided", true);
                              handleSubmitClick();
                            }}
                            disabled={
                              submitting ||
                              redirecting ||
                              watchPersonalGiftAid !== "true" ||
                              watchNoBenefitReceived !== "true"
                            }
                            data-cta-type="submit-with-gift-aid"
                          >
                            Continue with Gift Aid
                          </Button>
                          {showRefreshButton ? (
                            <Box marginTop="m" marginBottom="none">
                              <Text>
                                Going back caused a problem. Please refresh this
                                page to try again
                              </Text>
                              <Button
                                type="button"
                                appearance="primary"
                                onClick={() => {
                                  router?.reload();
                                }}
                                data-cta-type="refresh-page-on-error"
                              >
                                Refresh this page
                              </Button>
                            </Box>
                          ) : null}
                        </div>
                      </Box>
                    </form>
                    {submitionErrorMessage.length ? (
                      <Box>
                        <div className={sharedStyles["row-space-around"]}>
                          <ErrorText>{submitionErrorMessage}</ErrorText>
                        </div>
                      </Box>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ErrorPageWrapper>
  );
};

export default DonatePage;
